---
title: "Security Basics: 2FA, Phishing and Scams to Watch For"
slug: security-basics
shortTitle: "Security Basics"
description: "How to protect your trading account: a strong password, authenticator-app 2FA, spotting phishing pages and messages, and the scams that most often target traders."
level: beginner
category: crypto
course: beginner
order: 15
topics: [security]
publishedAt: 2026-10-19
updatedAt: 2026-10-19
featured: false
popular: false
cover: /academy/img/covers/security-basics.svg
faqs:
  - q: "Will staff ever ask for my password or 2FA code?"
    a: "No. Genuine staff never need your password, your authenticator codes or your backup codes. Anyone who asks for them, by email, chat, phone or social media, should be treated as a scammer."
  - q: "Why use an authenticator app instead of text-message codes?"
    a: "An authenticator app creates codes on your own phone, so they cannot be intercepted on the way to you. Text messages can be redirected if someone persuades your mobile provider to move your number to their SIM card."
  - q: "What should I do if I lose my phone with the authenticator app?"
    a: "Sign in with one of the backup codes you saved when you turned on 2FA, then set up the authenticator again on your new phone. Each backup code works once. If you have no backup codes left, contact support."
  - q: "How do I know if someone adds a withdrawal address to my account?"
    a: "We email you every time a withdrawal address, bank account or card is saved. If you didn't add it, change your password and contact support straight away."
  - q: "Someone offered to recover the money I lost to a scam. Is that real?"
    a: "Almost certainly not. Recovery scams target people who have already lost money and ask for an upfront fee or more crypto. Report the original scam to the police and your bank or exchange instead."
---

Your trading account holds real money and a direct route to withdraw it, which makes it a target. Yet most accounts are not lost to clever hacking. They are lost to tricks: a fake login page, a message from "support", or an offer that sounds too good to miss. Crypto adds one more twist, because a transfer sent to the wrong place usually cannot be reversed.

The good news is that a few habits block the large majority of attacks. This lesson covers your password, two-factor authentication (2FA), how to spot phishing, the scams aimed at traders, and what to do if something feels wrong.

<CTA label="Review your security settings" to="/account/security" />

<KeyTakeaways>

- **Turn on 2FA today.** An authenticator app adds a second lock that a stolen password alone cannot open.
- **Nobody legitimate needs your codes.** {{BRAND}} staff never ask for your password or 2FA codes; anyone who does is a scammer.
- **Check the address, not the look.** Phishing pages copy logos perfectly; the web address in your browser is what gives them away.
- **Promised returns are the biggest red flag.** Fixed daily profits, "managers" who trade for you and paid "recovery services" are classic scams.

</KeyTakeaways>

## Start with a strong, unique password

When you set a new password, {{BRAND}} asks for at least 8 characters with an upper-case letter, a lower-case letter and a digit. Treat that as the minimum. A passphrase of four or five random words is longer, stronger and easier to remember.

Most importantly, use a password you have never used anywhere else. When another website is breached, criminals try the leaked email and password pairs on other sites, a technique called **credential stuffing**. A password manager makes unique passwords painless.

Two safety features help you here. Changing your password signs out your other sessions, and password reset links expire after 30 minutes, so an old reset email in your inbox is useless to anyone else.

## Turn on two-factor authentication

**Two-factor authentication** asks for something you know (your password) and something you have (your phone). On {{BRAND}} it uses an **authenticator app**, which generates a fresh six-digit code every 30 seconds on your phone, even offline.

<Steps>
<Step title="Install an authenticator app">

Any standard authenticator app that supports time-based codes will work.

</Step>
<Step title="Open your security settings">

Go to Account → Personal information → **Security**, or open [/account/security](/account/security), and choose to set up the authenticator app.

</Step>
<Step title="Scan the QR code and confirm">

Scan the QR code with the app, then type the six-digit code it shows to prove the link works.

</Step>
<Step title="Save your backup codes">

You receive 10 backup codes. Each works once if you lose your phone. Write them down or store them offline, away from your phone.

</Step>
</Steps>

From then on you will need a code from the app to sign in and to request a withdrawal.

<Screenshot name="security-2fa-setup" alt="The Security tab showing the authenticator app setup with a QR code" caption="Account → Personal information → Security: setting up the authenticator app." />

<ProTip>

Changing phones? Move your authenticator to the new phone before you wipe the old one. If you are running low on backup codes, you can generate a fresh set from the same Security tab.

</ProTip>

## How to recognize phishing

**Phishing** is an attempt to trick you into handing over your login details, usually through a fake page that looks exactly like the real one. The message that leads you there might be an email, a text, a social media post, a search ad or a chat message. Watch for:

- **Urgency or threats:** "Your account will be frozen in 24 hours unless you verify now."
- **Lookalike web addresses:** an extra letter, swapped characters or a different ending after the dot.
- **Requests for secrets:** your password, 2FA codes or backup codes, or "log in through this link to confirm".
- **Unexpected downloads:** an app, browser extension or "remote support" tool you are asked to install.

The simplest defense is never to log in from a link. Type the address yourself or use a bookmark. If you get an alert about a **new sign-in from an unknown browser** and it was not you, change your password straight away and contact support through Account → Support.

## Scams that target traders

<CompareTable caption="Common scams and their giveaway signs">

| Scam | How it works | Red flag |
| --- | --- | --- |
| The "account manager" | Offers to trade for you, then asks for your login or remote access | Anyone asking to control your account |
| Fixed returns | A scheme promising steady daily or monthly profits | Returns that never have a losing period |
| Giveaways | "Send crypto, receive twice as much back", often using a famous name | Any offer that needs you to send first |
| Fake support | An "agent" contacts you first on social media or messaging apps | Support that finds you, not the other way round |
| Recovery services | Promise to get back lost funds for an upfront fee | Fees before anything is recovered |
| Online friends | A new contact gradually introduces an "amazing platform" | Investment talk from someone you have never met |

</CompareTable>

<Example title="Why fixed daily returns don't add up (illustrative numbers)">

A scheme promises 1% a day. Start with 1,000 USDT and compound it:

- After 30 days: 1,000 × 1.01^30 ≈ **1,348 USDT**
- After 365 days: 1,000 × 1.01^365 ≈ **37,783 USDT**

That is almost 38 times your money in a year, with no losing days. Real trading has losing days, weeks and months. Schemes that report smooth returns like these usually pay early members with later members' deposits until they collapse.

</Example>

## Protecting deposits and withdrawals

Crypto transfers are final, so a few extra checks are worth the seconds they take:

- **Match the network.** USDT deposits can arrive on TRC20, ERC20 or BEP20, but only on the network shown with the address you use. Withdrawals support USDT on TRC20 and ERC20, plus BTC and ETH, but not BEP20.
- **Check the whole address after pasting.** Some malware swaps a copied address for the attacker's. Compare the full string, not just the first few characters.
- **Watch for "new withdrawal address" emails.** We email you every time a withdrawal destination is saved. If you didn't add it, change your password and contact support straight away.

<Warning>

Sending crypto on the wrong network, or to a mistyped or swapped address, can mean the funds are lost for good. Nobody, including {{BRAND}}, can reverse a confirmed blockchain transfer. Check the network and the full address every time.

</Warning>

[Account Setup, Verification and Deposits](/academy/article/account-setup-deposits) and [Withdrawals, Fees and Account Security](/academy/article/withdrawals-fees-security) walk through both screens in detail.

## If you think your account is at risk

Act quickly and in this order:

1. **Change your password.** This also signs out other sessions.
2. **Check your 2FA.** Turn it on if it was off, and replace your backup codes if they may have been seen.
3. **Contact support** through Account → Support and explain what happened.
4. **Review your saved withdrawal destinations** and your recent money movements on the Portfolio page.
5. **Secure your email account.** It is the key to every password reset, so give it a unique password and 2FA too.

Ignore anyone who contacts you afterwards offering to recover your money. That is the next scam in line.

## Conclusion

Account security is mostly habits, not technology. Use a long, unique password, turn on an authenticator app and keep your backup codes offline. Never log in from a link, never share a code, and treat anyone who promises fixed returns or offers to trade for you as a scammer. Before any crypto transfer, check the network and the full address. Ten minutes of setup today protects everything you build later. If a beginner checklist helps, see [Common Beginner Mistakes](/academy/article/common-beginner-mistakes).
